import { useStorage } from '@vueuse/core';
import { isEqual } from 'lodash';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';
import { LocationQueryValue, useRoute, useRouter } from 'vue-router';

import { FEATURE_FLAG } from '@/constants/feature_flags';
import {
  PERFORMANCE_DASHBOARD_FILTERS_KEY,
  PERFORMANCE_DASHBOARD_FILTERS_WITH_PARTNER_SCORE_KEY,
} from '@/constants/storageKeys';
import { useFeatureFlagStore } from '@/stores/FeatureFlagStore';

import useAuth from './useAuth';

const VALID_SINGLE_FILTER_VALUES = {
  coverageType: ['partners', 'engagement'],
  opportunityType: ['all', 'net-new', 'existing'],
  timePeriod: ['thisQuarter', 'lastQuarter', 'yearToDate'],
  sort: [
    'overlapCount',
    'engagementActivitiesCount',
    'partnerImpact',
    'partnerScore',
    'attributionAmount',
  ],
} as const;

export type CoverageType =
  (typeof VALID_SINGLE_FILTER_VALUES.coverageType)[number];
export type OpportunityType =
  (typeof VALID_SINGLE_FILTER_VALUES.opportunityType)[number];
export type TimePeriod = (typeof VALID_SINGLE_FILTER_VALUES.timePeriod)[number];
export type SortField = (typeof VALID_SINGLE_FILTER_VALUES.sort)[number];
export type OpportunityOwnerType = string[];
export type AccountOwnerType = string[];
export type PartnerType = string[];
export type DealProductType = string[];

export type PerformanceDashboardSingleValueFilters = {
  [K in keyof typeof VALID_SINGLE_FILTER_VALUES]: (typeof VALID_SINGLE_FILTER_VALUES)[K][number];
};

export type PerformanceDashboardFilters = {
  coverageType: CoverageType;
  opportunityType: OpportunityType;
  timePeriod: TimePeriod;
  sort: SortField;
  deal_owner_id: string[] | null;
  partner: string[] | null;
  accountOwner: string[] | null;
  deal_product_type: string[] | null;
  partnerTag: string[] | null;
};

export type FilterVariant =
  | 'opportunityType'
  | 'timePeriod'
  | 'opportunityOwner'
  | 'accountOwner'
  | 'partner'
  | 'product'
  | 'partnerTag';

type ValidFilterKeys = keyof PerformanceDashboardFilters;

const ARRAY_FILTER_PARAMS = [
  'deal_owner_id',
  'partner',
  'accountOwner',
  'deal_product_type',
  'partnerTag',
] as const;

export const defaultFiltersWithoutPartnerScore: PerformanceDashboardFilters = {
  coverageType: 'partners',
  opportunityType: 'all',
  timePeriod: 'thisQuarter',
  sort: 'engagementActivitiesCount',
  deal_owner_id: null,
  partner: null,
  accountOwner: null,
  deal_product_type: null,
  partnerTag: null,
};

const defaultFiltersWithPartnerScore: PerformanceDashboardFilters = {
  coverageType: 'partners',
  opportunityType: 'all',
  timePeriod: 'thisQuarter',
  sort: 'partnerScore',
  deal_owner_id: null,
  partner: null,
  accountOwner: null,
  deal_product_type: null,
  partnerTag: null,
};

const VALID_KEYS = Object.keys(
  defaultFiltersWithPartnerScore,
) as ValidFilterKeys[];

const productNameToId = ref<Record<string, string>>({});

export function usePerformanceDashboardFilters() {
  const route = useRoute();
  const router = useRouter();
  const { currentOrg } = useAuth();
  const { hasFeatureFlag } = useFeatureFlagStore();

  const hasPartnerScoreAccess = computed(() =>
    hasFeatureFlag(FEATURE_FLAG.TEMP_PARTNER_SCORE_ACCESS),
  );

  const fiscalYearStart = computed(
    () => currentOrg.value?.fiscal_year_start ?? 1,
  );

  const defaultFilters = computed(() =>
    hasPartnerScoreAccess.value
      ? defaultFiltersWithPartnerScore
      : defaultFiltersWithoutPartnerScore,
  );

  // Use different storage keys based on FF state
  // This preserves user preferences for each mode separately
  const storageKey = computed(() =>
    hasPartnerScoreAccess.value
      ? PERFORMANCE_DASHBOARD_FILTERS_WITH_PARTNER_SCORE_KEY
      : PERFORMANCE_DASHBOARD_FILTERS_KEY,
  );

  const filters = useStorage<PerformanceDashboardFilters>(
    storageKey,
    defaultFilters.value,
    sessionStorage,
    { mergeDefaults: true },
  );

  const updateFilters = (newFilters: Partial<PerformanceDashboardFilters>) => {
    filters.value = {
      ...filters.value,
      ...newFilters,
    };
  };

  const syncFromQuery = () => {
    if (route.path !== '/measure/ecosystem') return;

    const updatedQuery = {} as Record<
      keyof PerformanceDashboardFilters,
      LocationQueryValue | LocationQueryValue[]
    >;
    let needsUrlFix = false;
    let hasInvalidFilters = false;
    const currentFilters = filters.value;

    // First pass: validate all filters
    for (const key of VALID_KEYS) {
      const raw = route.query[key];

      if (
        ARRAY_FILTER_PARAMS.includes(
          key as (typeof ARRAY_FILTER_PARAMS)[number],
        )
      ) {
        // Array parameters are always valid if present
        continue;
      }

      const val = typeof raw === 'string' ? raw : undefined;
      const allowed = (
        VALID_SINGLE_FILTER_VALUES as Record<string, readonly string[]>
      )[key] as readonly string[];

      if (val && !allowed.includes(val)) {
        hasInvalidFilters = true;
        break;
      }
    }

    // If any filter is invalid, reset all to session storage or defaults
    if (hasInvalidFilters) {
      filters.value = { ...currentFilters };
      Object.assign(updatedQuery, currentFilters);
      needsUrlFix = true;
    } else {
      // Second pass: apply valid filters
      for (const key of VALID_KEYS) {
        const raw = route.query[key];

        if (
          ARRAY_FILTER_PARAMS.includes(
            key as (typeof ARRAY_FILTER_PARAMS)[number],
          )
        ) {
          if (raw) {
            const arr = Array.isArray(raw) ? raw : [raw];
            filters.value = {
              ...filters.value,
              [key]: arr as PerformanceDashboardFilters[typeof key],
            };
            updatedQuery[key] = arr as LocationQueryValue[];
          } else {
            const fallback = currentFilters[key] ?? defaultFilters.value[key];
            filters.value = {
              ...filters.value,
              [key]: fallback as PerformanceDashboardFilters[typeof key],
            };
            updatedQuery[key] = fallback as LocationQueryValue;
            needsUrlFix = true;
          }
          continue;
        }

        const val = typeof raw === 'string' ? raw : undefined;
        const allowed = (
          VALID_SINGLE_FILTER_VALUES as Record<string, readonly string[]>
        )[key] as readonly string[];

        if (val && allowed.includes(val)) {
          filters.value = {
            ...filters.value,
            [key]: val as PerformanceDashboardFilters[typeof key],
          };
          updatedQuery[key] = val;
        } else {
          const fallback = filters.value[key] ?? defaultFilters.value[key];
          filters.value = {
            ...filters.value,
            [key]: fallback as PerformanceDashboardFilters[typeof key],
          };
          updatedQuery[key] = fallback;
          needsUrlFix = true;
        }
      }
    }

    if (needsUrlFix || Object.keys(route.query).length === 0) {
      router.replace({ query: updatedQuery });
    }
  };

  syncFromQuery();

  watch(
    filters,
    (newVal) => {
      const query: Record<string, LocationQueryValue | LocationQueryValue[]> = {
        ...route.query,
      };
      let changed = false;

      (
        Object.keys(newVal) as (keyof PerformanceDashboardSingleValueFilters)[]
      ).forEach((key) => {
        const raw = route.query[key];
        if (
          raw &&
          ARRAY_FILTER_PARAMS.includes(
            key as (typeof ARRAY_FILTER_PARAMS)[number],
          )
        ) {
          const asArray = Array.isArray(newVal[key])
            ? newVal[key]
            : [newVal[key]];

          if (isEqual(raw, asArray)) return;
          query[key] = asArray;
          changed = true;
          return;
        }

        if (query[key] !== newVal[key]) {
          query[key] = newVal[key];
          changed = true;
        }
      });

      if (changed) {
        router.replace({ query });
      }
    },
    { deep: true },
  );

  const formattedDates = computed(() => {
    const now = DateTime.local();
    let start: DateTime;
    let end: DateTime | undefined = undefined;
    const fiscalYearStartVal = fiscalYearStart.value;

    switch (filters.value.timePeriod) {
      case 'thisQuarter': {
        const quarterStartMonth =
          Math.floor((now.month - fiscalYearStartVal) / 3) * 3 +
          fiscalYearStartVal;
        start = DateTime.local()
          .set({ month: quarterStartMonth })
          .startOf('month');
        break;
      }
      case 'lastQuarter': {
        const lastQuarterStartMonth =
          Math.floor((now.month - fiscalYearStartVal - 3) / 3) * 3 +
          fiscalYearStartVal;
        start = DateTime.local()
          .set({ month: lastQuarterStartMonth })
          .startOf('month');
        end = start.plus({ months: 3 });
        break;
      }
      case 'yearToDate': {
        const fiscalStart = DateTime.local()
          .set({ month: fiscalYearStartVal })
          .startOf('month');
        start =
          fiscalStart > now ? fiscalStart.minus({ years: 1 }) : fiscalStart;
        break;
      }
      default: {
        start = now;
        end = now;
      }
    }

    return {
      startDate: start.toFormat('yyyy-MM'),
      endDate: end?.toFormat('yyyy-MM'),
    };
  });

  const fiscalYearStartDate = computed(() => {
    const now = DateTime.local();
    const fiscalYearStartVal = fiscalYearStart.value;

    const fiscalStart = DateTime.local()
      .set({ month: fiscalYearStartVal })
      .startOf('month');

    return (
      fiscalStart > now ? fiscalStart.minus({ years: 1 }) : fiscalStart
    ).toFormat('yyyy-MM');
  });

  const getValidFilterValues = (filterValue: string[] | null | undefined) => {
    // Handle the case where filterValue is an array with null values like [null]
    if (Array.isArray(filterValue)) {
      const validValues = filterValue.filter(
        (val) => val !== null && val !== undefined,
      );
      return validValues.length > 0 ? validValues : undefined;
    }
    // For non-array values, return undefined
    return undefined;
  };

  function updateProductTypeLookup(
    items: { product_id: string; product_type: string }[],
  ) {
    const map: Record<string, string> = {};
    for (const item of items) {
      map[item.product_type] = item.product_id;
    }
    productNameToId.value = map;
  }

  function productNamesToIds(names: string[]): string[] {
    return names.map((name) => productNameToId.value[name] ?? name);
  }

  return {
    filters,
    updateFilters,
    formattedDates,
    fiscalYearStartDate,
    getValidFilterValues,
    updateProductTypeLookup,
    productNamesToIds,
  };
}
